import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipContentProps } from "recharts";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import { AdminChartPanel } from "@/features/admin/components/layout";
import { ADMIN_LABEL_CLASS } from "@/features/admin/components/layout/admin-layout-tokens";
import { usePrefersReducedMotion } from "@/features/overview/components/OverviewChart/usePrefersReducedMotion";
import type {
  LlmUsageTenantOption,
  LlmUsageTimeseries,
} from "@/features/llm-usage/api/llm-usage.api";
import { formatEventWindowShort } from "@/features/events/constants/event-time-ranges";
import {
  formatCompactDurationMs,
  formatPreciseDurationMs,
} from "@/features/events/lib/event-timing";
import { tokensPerBucketAtAlertRate } from "../lib/alert-thresholds";
import { formatTokensCompact, formatTokensExact } from "../lib/format-tokens";
import { translateLlmUsageFeature } from "../lib/llm-usage-feature-labels";
import { brandForModelId, humanizeModelId } from "../lib/model-display";
import { ProviderIcon } from "./ProviderIcon";
import { SegmentedToggle } from "./SegmentedToggle";
import { TenantMark } from "./TenantMark";

interface LlmUsageChartProps {
  data: LlmUsageTimeseries | undefined;
  isLoading: boolean;
  /**
   * True while TanStack holds the previous window via `keepPreviousData`. The
   * entrance grow waits for the selected window's own payload — arming on the
   * range click alone would grow the stale series, latch, then paint the real
   * one with no motion.
   */
  isPlaceholderData: boolean;
  windowHours: number;
  /** The configured spend alert, or null when spend alerting is off. */
  tokenSpendAlert?: TokenSpendAlert | null;
  /** The configured p95 ceiling in ms, or null when latency alerting is off. */
  latencyAlertP95Ms?: number | null;
  /**
   * Branding for the tenant bands, from the same list that fills the tenant
   * filter. The timeseries itself carries only ids and names — logos live here
   * rather than being repeated on every bucket of every response.
   */
  tenantOptions?: LlmUsageTenantOption[];
  /**
   * What the bars open split by, from ops settings. Omit to open on token
   * class. The toggle still overrides it for the rest of the visit.
   */
  defaultStackBy?: StackBy;
}

/** A spend alert as configured: a token budget over its own rolling window. */
export interface TokenSpendAlert {
  threshold: number;
  windowHours: number;
}

/** Matches the Events activity chart, which this sits alongside in the same panel. */
const CHART_HEIGHT_PX = 132;
const TOKEN_AXIS_WIDTH = 44;
/** Wide enough for the longest single-unit duration, "999ms", at 10px. */
const LATENCY_AXIS_WIDTH = 40;
const CHART_TOP_MARGIN = 16;
const CHART_RIGHT_MARGIN = 4;
const Y_AXIS_TOP_PADDING = 8;
const AXIS_MIN_TICK_GAP = 28;

/**
 * Bars grow from the baseline via CSS (see `.chart-grow-enter`). Recharts' own
 * bar animation latches mid-flight on any parent re-render, which query updates
 * cause on every range change. Same approach as the Events activity chart.
 */
const ENTRANCE_MS = 300;

/**
 * Shorter than the entrance, and a rise rather than a grow: changing the
 * breakdown re-cuts bars that are already on screen, so replaying the full
 * grow-from-baseline would overstate what changed. An operator hunting a spike
 * flips through these, and the next click must never wait on the last one.
 */
const RESTACK_MS = 180;

/**
 * The categorical palette, not the semantic one: these are token classes, and
 * `--success` / `--warning` would imply a cache read is good and a cache write
 * is a problem. The line takes the one colour no bar uses so it reads as an
 * overlay rather than a sixth segment.
 */
const SERIES_COLORS = {
  inputTokens: "var(--chart-spend)",
  outputTokens: "var(--chart-purple)",
  thinkingTokens: "var(--chart-blue)",
  cacheWriteTokens: "var(--chart-olive)",
  cacheReadTokens: "var(--chart-teal)",
  p95: "var(--chart-orange)",
} as const;

/**
 * Breakdown slots come off the same categorical ramp, minus the orange the p95
 * line owns — a band wearing the line's colour would read as its area.
 * Assignment is by rank within the window, so the busiest band keeps its colour
 * while the range and filters change around it.
 */
const BREAKDOWN_COLORS = [
  "var(--chart-green)",
  "var(--chart-purple)",
  "var(--chart-yellow)",
  "var(--chart-teal)",
  "var(--chart-red)",
  "var(--chart-blue)",
  "var(--chart-olive)",
] as const;

/**
 * Past the palette, the remaining bands are folded into one grey one. Recycling
 * colours would make two different models look like the same series, and the
 * table below the chart still lists every model individually.
 */
const OTHER_KEY = "__other__";
const OTHER_COLOR = "var(--muted-foreground)";

/**
 * A limit is a warning, not a category, so it takes the semantic red rather
 * than a slot off the categorical ramp. The latency ceiling instead borrows the
 * orange its own line already owns, which ties the two to the right axis.
 */
const ALERT_LINE_COLOR = "var(--chart-red)";
const ALERT_LINE_OPACITY = 0.7;

/** Calls recorded before their call site was given an attribution. */
const UNATTRIBUTED_KEY = "__unattributed__";

/** Platform-level work no tenant asked for, such as the changelog digest. */
const NO_TENANT_KEY = "__no_tenant__";

/** Token class is the default: it is the only view that explains the bill. */
type StackBy = "class" | "model" | "feature" | "tenant";

const AXIS_TICK = { fontSize: 10, fill: "var(--muted-foreground)" } as const;

/** Stack order is billing order: what was sent, what came back, what it thought. */
const TOKEN_SERIES = [
  "inputTokens",
  "outputTokens",
  "thinkingTokens",
  "cacheWriteTokens",
  "cacheReadTokens",
] as const;

type TokenSeriesKey = (typeof TOKEN_SERIES)[number];

interface UsagePoint {
  bucketStart: string;
  label: string;
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  calls: number;
  failedCalls: number;
  p95LatencyMs: number | null;
  /**
   * Totals for whichever breakdown is stacked, keyed by model name or feature
   * label. Empty in token-class mode, which stacks the fields above instead.
   */
  stackValues: Record<string, number>;
}

/**
 * A band carries its own label because a tenant stacks by id — two orgs could
 * share a name, and merging them would be a reporting error rather than a
 * cosmetic one.
 */
interface UsageBand {
  key: string;
  label: string;
  totalTokens: number;
}

/**
 * The bands a bucket contributes to the active breakdown. Token-class mode
 * stacks fixed fields rather than a breakdown, so it contributes none.
 */
function bucketBands(bucket: LlmUsageTimeseries["buckets"][number], stackBy: StackBy): UsageBand[] {
  if (stackBy === "model") {
    return bucket.models.map((entry) => ({
      key: entry.model,
      label: entry.model,
      totalTokens: entry.totalTokens,
    }));
  }
  if (stackBy === "feature") {
    return bucket.features.map((entry) => ({
      key: entry.feature ?? UNATTRIBUTED_KEY,
      label: entry.feature ?? "",
      totalTokens: entry.totalTokens,
    }));
  }
  if (stackBy === "tenant") {
    return bucket.tenants.map((entry) => ({
      key: entry.tenantId ?? NO_TENANT_KEY,
      // A deleted tenant keeps its id, which is still traceable in the DB.
      label: entry.tenantName ?? entry.tenantId ?? "",
      totalTokens: entry.totalTokens,
    }));
  }
  return [];
}

/**
 * A bucket narrower than a day is a time; a day or wider is a date. Showing
 * "14:00" across a 30-day window would repeat the same handful of labels down
 * the axis and say nothing about which day.
 */
function bucketLabel(bucketStart: string, bucketMinutes: number, locale: string): string {
  const date = new Date(bucketStart);
  const MINUTES_PER_DAY = 60 * 24;

  return bucketMinutes >= MINUTES_PER_DAY
    ? date.toLocaleDateString(locale, { month: "short", day: "numeric" })
    : date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

interface UsageTooltipProps extends TooltipContentProps<number, string> {
  stackBy: StackBy;
  stackColors: Map<string, string>;
  stackLabels: Map<string, string>;
  tenantBranding: Map<string, LlmUsageTenantOption>;
}

/** The three synthetic keys that stand for something other than a real band. */
function isSyntheticBand(key: string): boolean {
  return key === OTHER_KEY || key === UNATTRIBUTED_KEY || key === NO_TENANT_KEY;
}

/** Small: the legend runs at 11px, and the mark should not outweigh the name. */
const TENANT_BAND_LOGO_SIZE = 14;

/**
 * What a band is, drawn: a tenant's logo, or its model's vendor glyph.
 *
 * Nothing rather than a blank spacer, unlike the tables: the legend and tooltip
 * run horizontally, so there is no column for a missing mark to keep aligned,
 * and an empty gap before "Other tenants" would just read as a failed image.
 *
 * A tenant band whose label is still the raw id — a tenant deleted since the
 * calls were made — gets no mark either, for the reason `TenantMark` gives:
 * initials derived from a cuid are worse than none.
 */
function BandMark({
  bandKey,
  stackBy,
  branding,
  labels,
}: {
  bandKey: string;
  stackBy: StackBy;
  branding: Map<string, LlmUsageTenantOption>;
  labels: Map<string, string>;
}) {
  if (isSyntheticBand(bandKey)) return null;

  if (stackBy === "model") {
    return <ProviderIcon brand={brandForModelId(bandKey)} className="size-3" />;
  }
  if (stackBy !== "tenant") return null;

  const tenant = branding.get(bandKey);
  const labelled = labels.get(bandKey);
  const name = tenant?.tenantName ?? (labelled && labelled !== bandKey ? labelled : null);
  if (name == null) return null;

  return (
    <TenantMark
      name={name}
      logoUrl={tenant?.logoUrl}
      brandColor={tenant?.brandColor}
      size={TENANT_BAND_LOGO_SIZE}
    />
  );
}

/**
 * Features get display copy because their recorded values are internal
 * identifiers an operator never chose to read. Models get their id spelled as a
 * name, matching the table below. A tenant name is already the org's own
 * wording, so it is shown verbatim.
 */
function bandLabel(key: string, stackBy: StackBy, t: TFunction, labels: Map<string, string>) {
  if (key === UNATTRIBUTED_KEY) return t("admin.llmUsage.chart.unattributed", "Unattributed");
  if (key === NO_TENANT_KEY) return t("admin.llmUsage.chart.noTenant", "No tenant");

  if (key === OTHER_KEY) {
    if (stackBy === "feature") return t("admin.llmUsage.chart.otherFeatures", "Other features");
    if (stackBy === "tenant") return t("admin.llmUsage.chart.otherTenants", "Other tenants");
    return t("admin.llmUsage.chart.otherModels", "Other models");
  }

  if (stackBy === "feature") return translateLlmUsageFeature(t, key);
  if (stackBy === "model") return humanizeModelId(key);
  return labels.get(key) ?? key;
}

/**
 * The exact model id behind a spelled-out name, since that is the string a rate
 * is keyed on. Every other band already shows all it has.
 */
function bandTitle(key: string, stackBy: StackBy): string | undefined {
  return stackBy === "model" && !isSyntheticBand(key) ? key : undefined;
}

function UsageTooltip({
  active,
  payload,
  stackBy,
  stackColors,
  stackLabels,
  tenantBranding,
}: UsageTooltipProps) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as UsagePoint | undefined;
  if (!point) return null;

  const labels: Record<TokenSeriesKey, string> = {
    inputTokens: t("admin.llmUsage.chart.input", "Input"),
    outputTokens: t("admin.llmUsage.chart.output", "Output"),
    thinkingTokens: t("admin.llmUsage.chart.thinking", "Thinking"),
    cacheWriteTokens: t("admin.llmUsage.chart.cacheWrite", "Cache write"),
    cacheReadTokens: t("admin.llmUsage.chart.cacheRead", "Cache read"),
  };

  // A zero row for a series this window never uses is noise, so each is listed
  // only when it carried something in this bucket.
  const rows =
    stackBy === "class"
      ? TOKEN_SERIES.filter((key) => point[key] > 0).map((key) => ({
          key,
          label: labels[key],
          value: formatTokensCompact(point[key], i18n.language),
          color: SERIES_COLORS[key],
        }))
      : [...stackColors.keys()]
          .filter((key) => (point.stackValues[key] ?? 0) > 0)
          .map((key) => ({
            key,
            label: bandLabel(key, stackBy, t, stackLabels),
            value: formatTokensCompact(point.stackValues[key] ?? 0, i18n.language),
            color: stackColors.get(key) ?? OTHER_COLOR,
          }));

  return (
    <div className="tooltip-enter relative z-50 rounded-md border border-border bg-card px-3 py-2 text-xs shadow-md">
      <p className="mb-1.5 font-medium text-foreground">{point.label}</p>
      {point.totalTokens === 0 ? (
        <p className="text-muted-foreground">{t("admin.llmUsage.chart.emptyBucket", "No calls")}</p>
      ) : (
        <ul className="space-y-0.5">
          {rows.map((row) => (
            <li key={row.key} className="flex items-center gap-1.5 whitespace-nowrap">
              <span
                aria-hidden
                className="size-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: row.color }}
              />
              <BandMark
                bandKey={row.key}
                stackBy={stackBy}
                branding={tenantBranding}
                labels={stackLabels}
              />
              <span className="text-muted-foreground">{row.label}</span>
              <span className="ml-auto pl-3 font-medium tabular-nums text-foreground">
                {row.value}
              </span>
            </li>
          ))}
          <li className="flex items-center gap-1.5 whitespace-nowrap border-t border-border pt-1 text-muted-foreground">
            <span>{t("admin.llmUsage.chart.total", "Total")}</span>
            <span className="ml-auto pl-3 font-medium tabular-nums text-foreground">
              {formatTokensCompact(point.totalTokens, i18n.language)}
            </span>
          </li>
          <li className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground">
            <span>{t("admin.llmUsage.chart.calls", "Calls")}</span>
            <span className="ml-auto pl-3 tabular-nums">
              {point.calls.toLocaleString(i18n.language)}
            </span>
          </li>
          {point.failedCalls > 0 ? (
            <li className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground">
              <span>{t("admin.llmUsage.chart.failures", "Failures")}</span>
              <span className="ml-auto pl-3 tabular-nums">
                {point.failedCalls.toLocaleString(i18n.language)}
              </span>
            </li>
          ) : null}
          {point.p95LatencyMs != null ? (
            <li className="flex items-center gap-1.5 whitespace-nowrap">
              <span
                aria-hidden
                className="size-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: SERIES_COLORS.p95 }}
              />
              <span className="text-muted-foreground">
                {t("admin.llmUsage.chart.p95", "p95 latency")}
              </span>
              <span className="ml-auto pl-3 font-medium tabular-nums text-foreground">
                {formatPreciseDurationMs(point.p95LatencyMs)}
              </span>
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}

function LegendDot({
  color,
  label,
  // Model and tenant names are proper nouns, not column headings: uppercasing
  // "Gemini 3.1 Pro Preview" shouts a name the table below prints normally.
  preserveCase = false,
  // A dash for the threshold lines, so the legend swatch matches the dashed
  // rule on the chart rather than implying another stacked band.
  variant = "dot",
  title,
  // Sits between the swatch and the name, so the colour still leads: the dot is
  // what ties the entry to a band, and a logo cannot do that job.
  mark,
}: {
  color: string;
  label: string;
  preserveCase?: boolean;
  variant?: "dot" | "dash";
  title?: string;
  mark?: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {variant === "dash" ? (
        <span
          aria-hidden
          className="h-0 w-2.5 shrink-0 border-t border-dashed"
          style={{ borderColor: color }}
        />
      ) : (
        <span
          aria-hidden
          className="size-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
      )}
      {mark}
      {/* Tenant names are arbitrary text, so without a ceiling one org can
          wrap its own label mid-word and orphan the dot above it. The cap sits
          past the longest model id and feature label so it only catches the
          runaway case, and `title` keeps the full name a hover away. */}
      <span
        title={title ?? label}
        className={cn(
          ADMIN_LABEL_CLASS,
          "max-w-[22ch] truncate",
          preserveCase && "normal-case tracking-normal",
        )}
      >
        {label}
      </span>
    </span>
  );
}

/**
 * Every billable token class stacked over the window the KPI strip totals, with
 * p95 latency over it. Stacked rather than grouped because the height is the
 * bill: the split matters, but the question the chart answers first is when the
 * spend happened. The latency line is what separates a busy window from a slow
 * one — the same pairing the Events activity chart makes.
 */
export function LlmUsageChart({
  data,
  isLoading,
  isPlaceholderData,
  windowHours,
  tokenSpendAlert = null,
  latencyAlertP95Ms = null,
  tenantOptions,
  defaultStackBy = "class",
}: LlmUsageChartProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const prefersReducedMotion = usePrefersReducedMotion();
  const [stackBy, setStackBy] = useState<StackBy>(defaultStackBy);

  // Ops settings arrive from their own query, so the configured split usually
  // lands a beat after the first paint. Tracking which default is already
  // applied adopts it when it does, while leaving a split the operator picked
  // by hand alone — that choice is newer than the setting.
  const [appliedDefault, setAppliedDefault] = useState<StackBy>(defaultStackBy);
  if (appliedDefault !== defaultStackBy) {
    setAppliedDefault(defaultStackBy);
    setStackBy(defaultStackBy);
  }

  // Re-arm the CSS entrance only when the selected window's own payload lands —
  // not on the range click (that still shows keepPreviousData) and not on
  // background refetches or feature-filter changes for the same window.
  const dataMatchesRequest = !isPlaceholderData && data != null && data.windowHours === windowHours;

  const [hasGrown, setHasGrown] = useState(false);
  const [armedWindow, setArmedWindow] = useState<number | null>(null);
  const [entranceEpoch, setEntranceEpoch] = useState(0);

  if (dataMatchesRequest && armedWindow !== windowHours) {
    setArmedWindow(windowHours);
    setHasGrown(false);
    setEntranceEpoch(entranceEpoch + 1);
  }

  const isEntranceActive = !prefersReducedMotion && !hasGrown && dataMatchesRequest;

  useEffect(() => {
    if (!isEntranceActive) return;
    const id = window.setTimeout(() => setHasGrown(true), ENTRANCE_MS + 40);
    return () => window.clearTimeout(id);
  }, [isEntranceActive, entranceEpoch]);

  // Re-cut bars animate by class rather than by remount: remounting would send
  // ResponsiveContainer back through a zero-width measure pass, and that flash
  // is worse than the snap this replaces.
  const [restackEpoch, setRestackEpoch] = useState(0);
  const [isRestacking, setIsRestacking] = useState(false);
  const isRestackActive = !prefersReducedMotion && isRestacking && !isEntranceActive;

  useEffect(() => {
    if (!isRestacking) return;
    const id = window.setTimeout(() => setIsRestacking(false), RESTACK_MS + 40);
    return () => window.clearTimeout(id);
  }, [isRestacking, restackEpoch]);

  const handleStackByChange = (value: StackBy) => {
    setStackBy(value);
    setRestackEpoch((epoch) => epoch + 1);
    setIsRestacking(true);
  };

  // Ranked by window total so the stack order and colours stay put as buckets
  // come and go on refetch, rather than reshuffling on whichever band happens
  // to lead the newest bucket.
  const { stackKeys, stackLabels } = useMemo(() => {
    if (!data || stackBy === "class") {
      return { stackKeys: [] as string[], stackLabels: new Map<string, string>() };
    }
    const totals = new Map<string, number>();
    const labels = new Map<string, string>();
    for (const bucket of data.buckets) {
      for (const band of bucketBands(bucket, stackBy)) {
        totals.set(band.key, (totals.get(band.key) ?? 0) + band.totalTokens);
        labels.set(band.key, band.label);
      }
    }
    const ranked = [...totals.entries()].sort(([, a], [, b]) => b - a).map(([key]) => key);

    return {
      stackKeys:
        ranked.length > BREAKDOWN_COLORS.length
          ? [...ranked.slice(0, BREAKDOWN_COLORS.length), OTHER_KEY]
          : ranked,
      stackLabels: labels,
    };
  }, [data, stackBy]);

  const stackColors = useMemo(
    () =>
      new Map(
        stackKeys.map((key, index) => [
          key,
          key === OTHER_KEY ? OTHER_COLOR : BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length],
        ]),
      ),
    [stackKeys],
  );

  const points = useMemo<UsagePoint[]>(() => {
    if (!data) return [];
    const named = new Set(stackKeys);
    return data.buckets.map((bucket) => {
      const stackValues: Record<string, number> = {};
      for (const band of bucketBands(bucket, stackBy)) {
        const key = named.has(band.key) ? band.key : OTHER_KEY;
        stackValues[key] = (stackValues[key] ?? 0) + band.totalTokens;
      }
      return {
        bucketStart: bucket.bucketStart,
        label: bucketLabel(bucket.bucketStart, data.bucketMinutes, locale),
        inputTokens: bucket.inputTokens,
        outputTokens: bucket.outputTokens,
        thinkingTokens: bucket.thinkingTokens,
        cacheWriteTokens: bucket.cacheWriteTokens,
        cacheReadTokens: bucket.cacheReadTokens,
        totalTokens: bucket.totalTokens,
        calls: bucket.calls,
        failedCalls: bucket.failedCalls,
        p95LatencyMs: bucket.p95LatencyMs,
        stackValues,
      };
    });
  }, [data, locale, stackBy, stackKeys]);

  const total = points.reduce((sum, point) => sum + point.totalTokens, 0);
  const failedTotal = points.reduce((sum, point) => sum + point.failedCalls, 0);
  const hasFailures = failedTotal > 0;

  // A series no model in this window produced gets no bar and no legend entry:
  // Gemini reports no cache writes, Anthropic no thinking tokens, and a legend
  // full of flat zeros reads as missing data rather than as absent behavior.
  const activeSeries = useMemo(
    () => TOKEN_SERIES.filter((key) => points.some((point) => point[key] > 0)),
    [points],
  );
  const hasLatency = points.some((point) => point.p95LatencyMs != null);

  const tenantBranding = useMemo(
    () => new Map((tenantOptions ?? []).map((option) => [option.tenantId, option])),
    [tenantOptions],
  );

  // Restated as a per-bucket rate, because the alert's window and the chart's
  // bucket are set independently and rarely agree.
  const spendAlertPerBucket = useMemo(
    () =>
      tokenSpendAlert && data
        ? tokensPerBucketAtAlertRate(
            tokenSpendAlert.threshold,
            tokenSpendAlert.windowHours,
            data.bucketMinutes,
          )
        : null,
    [tokenSpendAlert, data],
  );
  // Nothing to hang the line on when the window recorded no latency at all.
  const latencyAlert = hasLatency && latencyAlertP95Ms && latencyAlertP95Ms > 0
    ? latencyAlertP95Ms
    : null;

  const summary = t(
    "admin.llmUsage.chart.summary",
    "Token usage over the last {{window}}: {{total}} tokens.",
    {
      window: formatEventWindowShort(windowHours, t),
      total: formatTokensExact(total, locale),
    },
  );

  const seriesLabels: Record<TokenSeriesKey, string> = {
    inputTokens: t("admin.llmUsage.chart.input", "Input"),
    outputTokens: t("admin.llmUsage.chart.output", "Output"),
    thinkingTokens: t("admin.llmUsage.chart.thinking", "Thinking"),
    cacheWriteTokens: t("admin.llmUsage.chart.cacheWrite", "Cache write"),
    cacheReadTokens: t("admin.llmUsage.chart.cacheRead", "Cache read"),
  };

  return (
    <AdminChartPanel
      title={t("admin.llmUsage.chart.title", "Token usage")}
      height={CHART_HEIGHT_PX}
      isLoading={isLoading && points.length === 0}
      isEmpty={total === 0 && failedTotal === 0}
      emptyMessage={t("admin.llmUsage.chart.empty", "No LLM calls in this window")}
      legend={
        <>
          {stackBy === "class"
            ? activeSeries.map((key) => (
                <LegendDot key={key} color={SERIES_COLORS[key]} label={seriesLabels[key]} />
              ))
            : stackKeys.map((key) => (
                <LegendDot
                  key={key}
                  color={stackColors.get(key) ?? OTHER_COLOR}
                  label={bandLabel(key, stackBy, t, stackLabels)}
                  preserveCase={!isSyntheticBand(key)}
                  title={bandTitle(key, stackBy)}
                  mark={
                    <BandMark
                      bandKey={key}
                      stackBy={stackBy}
                      branding={tenantBranding}
                      labels={stackLabels}
                    />
                  }
                />
              ))}
          {hasLatency ? (
            <LegendDot
              color={SERIES_COLORS.p95}
              label={t("admin.llmUsage.chart.p95", "p95 latency")}
            />
          ) : null}
          {hasFailures ? (
            <LegendDot
              color="var(--destructive)"
              label={t("admin.llmUsage.chart.failures", "Failures")}
            />
          ) : null}
          {/* The line can sit off-scale, so the legend carries the number: an
              enabled alert is always accounted for somewhere on the header. */}
          {spendAlertPerBucket != null && tokenSpendAlert ? (
            <LegendDot
              variant="dash"
              color={ALERT_LINE_COLOR}
              label={t("admin.llmUsage.chart.spendAlert", "Spend alert")}
              title={t(
                "admin.llmUsage.chart.spendAlertTitle",
                "Alerts above {{threshold}} tokens per {{window}}. The line marks that rate over one bar: {{perBucket}} tokens.",
                {
                  threshold: formatTokensExact(tokenSpendAlert.threshold, locale),
                  window: formatEventWindowShort(tokenSpendAlert.windowHours, t),
                  perBucket: formatTokensExact(Math.round(spendAlertPerBucket), locale),
                },
              )}
            />
          ) : null}
          {latencyAlert != null ? (
            <LegendDot
              variant="dash"
              color={SERIES_COLORS.p95}
              label={t("admin.llmUsage.chart.latencyAlert", "Latency alert")}
              title={t(
                "admin.llmUsage.chart.latencyAlertTitle",
                "Alerts when p95 latency passes {{threshold}}.",
                { threshold: formatPreciseDurationMs(latencyAlert) },
              )}
            />
          ) : null}
        </>
      }
      controls={
        <SegmentedToggle<StackBy>
          value={stackBy}
          onValueChange={handleStackByChange}
          ariaLabel={t("admin.llmUsage.chart.stackBy", "Stack by")}
          options={[
            { value: "class", label: t("admin.llmUsage.chart.stackByClass", "Token class") },
            { value: "model", label: t("admin.llmUsage.chart.stackByModel", "Model") },
            { value: "feature", label: t("admin.llmUsage.chart.stackByFeature", "Feature") },
            { value: "tenant", label: t("admin.llmUsage.chart.stackByTenant", "Tenant") },
          ]}
        />
      }
    >
      <div
        // Remount when a matching window payload arms the entrance so the
        // CSS grow runs on the real series, not a keepPreviousData holdover.
        key={entranceEpoch}
        className={cn(
          isEntranceActive && "chart-grow-enter",
          isRestackActive && "chart-restack-enter",
        )}
      >
        <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
          <ComposedChart
            data={points}
            margin={{ top: CHART_TOP_MARGIN, right: CHART_RIGHT_MARGIN, left: 0, bottom: 0 }}
            title={summary}
          >
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
            {/* Left off `ifOverflow="extendDomain"` on purpose. A ceiling
                set far above current spend would drag the axis up to meet
                it and flatten every bar to a stub, costing the chart the
                spike-spotting it exists for. Off-scale means far from the
                limit, and the legend still names the limit either way. */}
            {spendAlertPerBucket != null ? (
              <ReferenceLine
                yAxisId="tokens"
                y={spendAlertPerBucket}
                stroke={ALERT_LINE_COLOR}
                strokeOpacity={ALERT_LINE_OPACITY}
                strokeDasharray="4 4"
              />
            ) : null}
            {latencyAlert != null ? (
              <ReferenceLine
                yAxisId="latency"
                y={latencyAlert}
                stroke={SERIES_COLORS.p95}
                strokeOpacity={ALERT_LINE_OPACITY}
                strokeDasharray="4 4"
              />
            ) : null}
            <XAxis
              dataKey="label"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={{ stroke: "var(--border)" }}
              interval="preserveStartEnd"
              minTickGap={AXIS_MIN_TICK_GAP}
            />
            <YAxis
              yAxisId="tokens"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={TOKEN_AXIS_WIDTH}
              allowDecimals={false}
              tickFormatter={(value: number) => formatTokensCompact(value, locale)}
              padding={{ top: Y_AXIS_TOP_PADDING, bottom: 0 }}
            />
            {hasLatency ? (
              <YAxis
                yAxisId="latency"
                orientation="right"
                tick={{ ...AXIS_TICK, fill: SERIES_COLORS.p95 }}
                tickLine={false}
                axisLine={false}
                width={LATENCY_AXIS_WIDTH}
                tickFormatter={(value: number) => formatCompactDurationMs(value)}
                padding={{ top: Y_AXIS_TOP_PADDING, bottom: 0 }}
              />
            ) : null}
            {hasFailures ? (
              <YAxis
                yAxisId="failures"
                orientation="right"
                hide
                domain={[0, "auto"]}
              />
            ) : null}
            <Tooltip
              content={(props) => (
                <UsageTooltip
                  {...(props as TooltipContentProps<number, string>)}
                  stackBy={stackBy}
                  stackColors={stackColors}
                  stackLabels={stackLabels}
                  tenantBranding={tenantBranding}
                />
              )}
              cursor={{ fill: "var(--foreground)", fillOpacity: 0.04 }}
              // Above sticky table heads (z-[1]) when the tip overflows the plot,
              // and above SVG bars/grid that otherwise paint over the HTML tip.
              wrapperStyle={{ zIndex: 50 }}
            />
            {stackBy === "class"
              ? activeSeries.map((key) => (
                  <Bar
                    key={key}
                    yAxisId="tokens"
                    dataKey={key}
                    stackId="tokens"
                    fill={SERIES_COLORS[key]}
                    // Recharts JS grow is disabled — CSS `.chart-grow-enter`
                    // owns the entrance (see ENTRANCE_MS).
                    isAnimationActive={false}
                  />
                ))
              : stackKeys.map((key) => (
                  <Bar
                    key={key}
                    yAxisId="tokens"
                    // A function key, because model names carry dots that
                    // Recharts would read as a nested path.
                    dataKey={(point: UsagePoint) => point.stackValues[key] ?? 0}
                    name={key}
                    stackId="tokens"
                    fill={stackColors.get(key) ?? OTHER_COLOR}
                    isAnimationActive={false}
                  />
                ))}
            {hasLatency ? (
              <Line
                yAxisId="latency"
                type="monotone"
                dataKey="p95LatencyMs"
                stroke={SERIES_COLORS.p95}
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 2.5, strokeWidth: 0 }}
                isAnimationActive={false}
                connectNulls
              />
            ) : null}
            {hasFailures ? (
              <Line
                yAxisId="failures"
                type="monotone"
                dataKey="failedCalls"
                name={t("admin.llmUsage.chart.failures", "Failures")}
                stroke="var(--destructive)"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                isAnimationActive={false}
              />
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </AdminChartPanel>
  );
}
